import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams, Routes, Route, Navigate } from 'react-router-dom';
import {
  Search,
  Filter,
  UserPlus,
  ChevronRight,
  User,
  Users
} from 'lucide-react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { searchCustomers } from '../../services/dashboardApi';
import LedgerView from './LedgerView';
import AddPartyModal from './AddPartyModal';

const PartiesHome = ({ initialTab = 'customers', setActiveTab: setDashboardTab }) => {
  const { t } = useLang();
  const [selectedParty, setSelectedParty] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab); // 'customers' or 'suppliers'
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  const [searchQuery, setSearchQuery] = useState('');
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOption, setFilterOption] = useState('all'); // 'all', 'get', 'give'
  const [sortOption, setSortOption] = useState('latest'); // 'latest', 'nameAZ', 'nameZA', 'highToLow', 'lowToHigh'
  const [showFilterModal, setShowFilterModal] = useState(false);

  useEffect(() => {
    fetchParties();
  }, []);

  const fetchParties = async () => {
    try {
      setLoading(true);
      const data = await searchCustomers();
      if (data?.success) {
        setParties(data.customers || []);
      }
    } catch (error) {
      console.error('Error fetching parties:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedParties = useMemo(() => {
    let list = parties.filter(p => {
      const typeMatch = activeTab === 'customers'
        ? p.type?.toLowerCase() === 'customer'
        : p.type?.toLowerCase() === 'supplier';

      const searchMatch = p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.mobile?.includes(searchQuery);

      let filterMatch = true;
      if (filterOption === 'get') filterMatch = parseFloat(p.balance) > 0;
      if (filterOption === 'give') filterMatch = parseFloat(p.balance) < 0;

      return typeMatch && searchMatch && filterMatch;
    });

    list.sort((a, b) => {
      switch (sortOption) {
        case 'nameAZ':
          return a.name.localeCompare(b.name);
        case 'nameZA':
          return b.name.localeCompare(a.name);
        case 'highToLow':
          return Math.abs(parseFloat(b.balance)) - Math.abs(parseFloat(a.balance));
        case 'lowToHigh':
          return Math.abs(parseFloat(a.balance)) - Math.abs(parseFloat(b.balance));
        case 'latest':
        default:
          const dateA = new Date(a.last_transaction || a.created_at || 0).getTime();
          const dateB = new Date(b.last_transaction || b.created_at || 0).getTime();
          if (dateA !== dateB) return dateB - dateA;
          return (b.id || 0) - (a.id || 0); // Fallback to ID for newly created without timestamps yet
      }
    });

    return list;
  }, [parties, activeTab, searchQuery, filterOption, sortOption]);

  const formatTimeAgo = (dateStr) => {
    if (!dateStr || dateStr === "null" || dateStr === "0000-00-00 00:00:00") return t(LangKeys.no_transactions);
    try {
      const date = new Date(dateStr.replace(' ', 'T'));
      if (isNaN(date.getTime())) return t(LangKeys.no_transactions);
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);
      if (diffInSeconds < 60) return t(LangKeys.just_now);
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      if (diffInMinutes < 60) return t(LangKeys.min_ago).replace('{value}', diffInMinutes);
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return t(LangKeys.hr_ago).replace('{value}', diffInHours);
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 30) return t(LangKeys.day_ago).replace('{value}', diffInDays);
      const diffInMonths = Math.floor(diffInDays / 30);
      if (diffInMonths < 12) return t(LangKeys.month_ago).replace('{value}', diffInMonths);
      return t(LangKeys.year_ago).replace('{value}', Math.floor(diffInMonths / 12));
    } catch (e) { return t(LangKeys.no_transactions); }
  };

  const getAvatarColor = (name) => {
    const colors = [
      { bg: '#EFF6FF', text: '#1D4ED8' }, { bg: '#F0FDF4', text: '#16A34A' },
      { bg: '#FFF7ED', text: '#C2410C' }, { bg: '#FAF5FF', text: '#7E22CE' },
      { bg: '#FFF1F2', text: '#BE123C' }, { bg: '#FDF2F8', text: '#BE185D' },
      { bg: '#F0FDFA', text: '#0F766E' },
    ];
    return colors[(name?.charCodeAt(0) || 0) % colors.length];
  };

  const stats = useMemo(() => {
    const relevantParties = parties.filter(p =>
      activeTab === 'customers' ? p.type?.toLowerCase() === 'customer' : p.type?.toLowerCase() === 'supplier'
    );
    let youWillGet = 0; let youWillGive = 0;
    relevantParties.forEach(p => {
      const balance = parseFloat(p.balance) || 0;
      if (balance > 0) youWillGet += balance;
      else youWillGive += Math.abs(balance);
    });
    return { youWillGet, youWillGive };
  }, [parties, activeTab]);

  return (
    <div className="print-main-wrapper" style={styles.mainWrapper}>
      <style>{`
        @media print {
          div[style*="leftColumn"] {
            display: none !important;
          }
          div[style*="rightColumn"] {
            width: 100% !important;
            flex: none !important;
            padding: 0 !important;
            margin: 0 !important;
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            background: white !important;
          }
          /* Hide the floating card wrapper styles during print */
          div[style*="ledgerCardWrap"] {
            border: none !important;
            box-shadow: none !important;
            padding: 0 !important;
            margin: 0 !important;
            border-radius: 0 !important;
          }
          body, html, #root {
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
          }
        }
      `}</style>
      {/* LEFT SIDE: LIST (approx 40%) */}
      <div className="no-print" style={styles.leftColumn}>
        <div style={styles.container}>
          {/* STATS SUMMARY CARD */}
          <div style={styles.statsCard}>
            <div style={styles.statBox}>
              <span style={styles.statLabel}>{t(LangKeys.you_will_get)}</span>
              <span style={{ ...styles.statValue, color: '#16A34A' }}>₹{stats.youWillGet.toLocaleString()}</span>
            </div>
            <div style={styles.statDivider} />
            <div style={styles.statBox}>
              <span style={styles.statLabel}>{t(LangKeys.you_will_give)}</span>
              <span style={{ ...styles.statValue, color: '#DC2626' }}>₹{stats.youWillGive.toLocaleString()}</span>
            </div>
          </div>

          {/* TABS */}
          <div style={styles.tabContainer}>
            <button
              style={{ ...styles.tab, ...(activeTab === 'customers' ? styles.activeTab : {}) }}
              onClick={() => setActiveTab('customers')}
            >
              <User size={18} />
              <span>{t(LangKeys.customers)}</span>
            </button>
            <button
              style={{ ...styles.tab, ...(activeTab === 'suppliers' ? styles.activeTab : {}) }}
              onClick={() => setActiveTab('suppliers')}
            >
              <Users size={18} />
              <span>{t(LangKeys.suppliers)}</span>
            </button>
          </div>

          {/* SEARCH & FILTER BAR */}
          <div style={styles.searchBar}>
            <div style={styles.searchInputWrap}>
              <Search size={18} color="#64748B" />
              <input
                type="text"
                placeholder={t(activeTab === 'customers' ? LangKeys.search_customer : LangKeys.search_supplier)}
                style={styles.searchInput}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button
              style={{ ...styles.filterBtn, ...(filterOption !== 'all' || sortOption !== 'latest' ? styles.filterActive : {}) }}
              onClick={() => setShowFilterModal(true)}
            >
              <Filter size={18} />
            </button>
          </div>

          {/* LIST SECTION */}
          <div style={styles.listContainer}>
            {loading ? (
              <div style={styles.center}>
                <div className="spinner"></div>
              </div>
            ) : filteredAndSortedParties.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>
                  {activeTab === 'customers' ? <User size={48} color="#CBD5E1" /> : <Users size={48} color="#CBD5E1" />}
                </div>
                <h3 style={styles.emptyTitle}>
                  {activeTab === 'customers' ? t(LangKeys.no_customers_found) : t(LangKeys.no_suppliers_found)}
                </h3>
              </div>
            ) : (
              filteredAndSortedParties.map((party) => {
                const avatarStyle = getAvatarColor(party.name);
                const isSelected = selectedParty?.id === party.id;
                return (
                  <div
                    key={party.id}
                    tabIndex={-1}
                    onMouseDown={e => e.preventDefault()}
                    style={{
                      ...styles.partyItem,
                      ...(isSelected ? styles.partyItemActive : {})
                    }}
                    onClick={() => setSelectedParty(party)}
                  >
                    <div style={styles.partyLeft}>
                      <div style={{ ...styles.avatar, backgroundColor: avatarStyle.bg, color: avatarStyle.text }}>
                        {party.name?.charAt(0).toUpperCase()}
                      </div>
                      <div style={styles.partyInfo}>
                        <h4 style={styles.partyName}>{party.name}</h4>
                        <span style={styles.partyMeta}>
                          {formatTimeAgo(party.last_transaction || party.created_at)}
                        </span>
                      </div>
                    </div>
                    <div style={styles.partyRight}>
                      <div style={styles.balanceInfo}>
                        <span style={{
                          ...styles.balanceAmount,
                          color: parseFloat(party.balance) >= 0 ? '#10B981' : '#EF4444'
                        }}>
                          ₹{Math.abs(parseFloat(party.balance)).toLocaleString()}
                        </span>
                        <span style={styles.balanceStatus}>
                          {parseFloat(party.balance) === 0 ? t(LangKeys.settled) :
                            parseFloat(party.balance) > 0 ? t(LangKeys.advance) : t(LangKeys.due)}
                        </span>
                      </div>
                      <ChevronRight size={18} color={isSelected ? '#1D4ED8' : '#94A3B8'} />
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* ADD BUTTON (LEFT SIDE) */}
          <button 
            style={{
              ...styles.addBtn,
              backgroundColor: activeTab === 'customers' ? '#1D4ED8' : '#16A34A'
            }}
            onClick={() => setShowAddModal(true)}
          >
            <UserPlus size={20} />
            <span>{activeTab === 'customers' ? t(LangKeys.add_customer) : t(LangKeys.add_supplier)}</span>
          </button>

          <AddPartyModal 
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            initialType={activeTab === 'suppliers' ? 'supplier' : 'customer'}
            onSuccess={(newParty) => {
              setShowAddModal(false);
              fetchParties(); 
              if (newParty) {
                const isSup = newParty.is_supplier === 1 || newParty.is_supplier === '1' || newParty.type?.toLowerCase() === 'supplier';
                setActiveTab(isSup ? 'suppliers' : 'customers');
              }
            }}
          />
        </div>
      </div>

      {/* RIGHT SIDE: LEDGER (approx 60%) */}
      <div className="print-right-column" style={styles.rightColumn}>
        {selectedParty ? (
          <div className="print-ledger-card-wrap" style={styles.ledgerCardWrap}>
            <LedgerView
              key={selectedParty.id}
              party={selectedParty}
              isSupplier={selectedParty.type?.toLowerCase() === 'supplier'}
              onBack={() => setSelectedParty(null)}
              setActiveTab={setDashboardTab}
            />
          </div>
        ) : (
          <div style={styles.placeholderContainer}>
            <div style={styles.placeholderIconBox}>
              <User size={64} color="#E2E8F0" />
            </div>
          </div>
        )}
      </div>

      {/* FILTER MODAL OVERLAY */}
      {showFilterModal && (
        <div style={styles.modalOverlay} onClick={() => setShowFilterModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>{t(LangKeys.filter_and_sort)}</h3>
              <button style={styles.closeBtn} onClick={() => setShowFilterModal(false)}>×</button>
            </div>

            <div style={styles.modalSection}>
              <span style={styles.sectionTitle}>{t(LangKeys.filter_by_balance)}</span>
              <div style={styles.optionGroup}>
                <button
                  style={{ ...styles.option, ...(filterOption === 'all' ? styles.optionActive : {}) }}
                  onClick={() => setFilterOption('all')}
                >{t(LangKeys.filter_all_parties)}</button>
                <button
                  style={{ ...styles.option, ...(filterOption === 'get' ? styles.optionActive : {}) }}
                  onClick={() => setFilterOption('get')}
                >{t(LangKeys.you_will_get)}</button>
                <button
                  style={{ ...styles.option, ...(filterOption === 'give' ? styles.optionActive : {}) }}
                  onClick={() => setFilterOption('give')}
                >{t(LangKeys.you_will_give)}</button>
              </div>
            </div>

            <div style={styles.modalSection}>
              <span style={styles.sectionTitle}>{t(LangKeys.sort_by)}</span>
              <div style={styles.optionGroup}>
                <button
                  style={{ ...styles.option, ...(sortOption === 'latest' ? styles.optionActive : {}) }}
                  onClick={() => setSortOption('latest')}
                >{t(LangKeys.sort_latest_first)}</button>
                <button
                  style={{ ...styles.option, ...(sortOption === 'nameAZ' ? styles.optionActive : {}) }}
                  onClick={() => setSortOption('nameAZ')}
                >{t(LangKeys.sort_by_name_az)}</button>
                <button
                  style={{ ...styles.option, ...(sortOption === 'nameZA' ? styles.optionActive : {}) }}
                  onClick={() => setSortOption('nameZA')}
                >{t(LangKeys.sort_by_name_za)}</button>
                <button
                  style={{ ...styles.option, ...(sortOption === 'highToLow' ? styles.optionActive : {}) }}
                  onClick={() => setSortOption('highToLow')}
                >{t(LangKeys.sort_highest_amount)}</button>
              </div>
            </div>

            <button style={styles.applyBtn} onClick={() => setShowFilterModal(false)}>{t(LangKeys.apply_filters)}</button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  mainWrapper: {
    display: 'flex',
    height: 'calc(100vh - 80px)',
    background: '#F8FAFC',
    overflow: 'hidden',
  },
  leftColumn: {
    width: '420px',
    borderRight: '1px solid #E2E8F0',
    display: 'flex',
    flexDirection: 'column',
    background: '#FFFFFF',
    flexShrink: 0,
    position: 'relative',
  },
  rightColumn: {
    flex: 1,
    background: '#F8FAFC',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    padding: '24px',
  },
  ledgerCardWrap: {
    flex: 1,
    background: '#FFFFFF',
    borderRadius: '24px',
    border: '1px solid #E2E8F0',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
  },
  container: {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    overflowY: 'auto',
    height: '100%',
    paddingBottom: '100px',
  },
  statsCard: {
    display: 'flex',
    background: '#FFFFFF',
    borderRadius: '20px',
    padding: '20px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
    border: '1px solid #F1F5F9',
  },
  statBox: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  statLabel: {
    fontSize: '11px',
    color: '#64748B',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
  },
  statValue: {
    fontSize: '18px',
    fontWeight: '800',
  },
  statDivider: {
    width: '1px',
    background: '#F1F5F9',
    margin: '0 15px',
  },
  tabContainer: {
    display: 'flex',
    gap: '4px',
    background: '#F1F5F9',
    padding: '4px',
    borderRadius: '12px',
    width: '100%',
  },
  tab: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px',
    borderRadius: '8px',
    border: 'none',
    background: 'transparent',
    color: '#64748B',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '13px',
  },
  activeTab: {
    background: '#FFFFFF',
    color: '#1D4ED8',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  },
  searchBar: {
    display: 'flex',
    gap: '10px',
  },
  searchInputWrap: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: '#F8FAFC',
    padding: '0 16px',
    borderRadius: '12px',
    border: '1px solid #E2E8F0',
    height: '44px',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    color: '#0F172A',
    fontWeight: '500',
    background: 'transparent',
  },
  filterBtn: {
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#FFFFFF',
    borderRadius: '12px',
    border: '1px solid #E2E8F0',
    cursor: 'pointer',
    color: '#475569',
  },
  filterActive: {
    borderColor: '#1D4ED8',
    color: '#1D4ED8',
    background: '#EFF6FF',
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  partyItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    background: '#FFFFFF',
    borderRadius: '14px',
    border: 'none !important',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none !important',
    boxShadow: 'none',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    WebkitFocusRingColor: 'transparent',
  },
  partyItemActive: {
    background: '#EFF6FF',
    border: '1px solid #DBEAFE !important',
    boxShadow: '0 4px 12px rgba(29, 78, 216, 0.05)',
  },
  partyLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  avatar: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: '800',
  },
  partyInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  partyName: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#0F172A',
    margin: 0,
  },
  partyMeta: {
    fontSize: '12px',
    color: '#94A3B8',
    fontWeight: '600',
  },
  partyRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  balanceInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  balanceAmount: {
    fontSize: '15px',
    fontWeight: '800',
  },
  addBtn: {
    position: 'absolute',
    bottom: '24px',
    left: '20px',
    right: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '14px',
    borderRadius: '12px',
    border: 'none',
    color: '#FFFFFF',
    fontSize: '15px',
    fontWeight: '800',
    cursor: 'pointer',
    boxShadow: '0 10px 25px rgba(29, 78, 216, 0.2)',
    zIndex: 10,
  },
  placeholderContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    textAlign: 'center',
    background: '#F1F5F9',
  },
  placeholderIconBox: {
    width: '120px',
    height: '120px',
    background: '#FFFFFF',
    borderRadius: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '24px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.03)',
  },
  placeholderTitle: {
    fontSize: '22px',
    fontWeight: '800',
    color: '#1E293B',
    margin: '0 0 12px 0',
  },
  placeholderSub: {
    fontSize: '15px',
    color: '#64748B',
    maxWidth: '300px',
    lineHeight: '1.6',
    margin: 0,
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    padding: '40px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
  },
  emptyIcon: {
    marginBottom: '16px',
    padding: '16px',
    borderRadius: '20px',
    background: '#F1F5F9',
  },
  emptyTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#475569',
    margin: 0,
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.4)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modalContent: {
    background: '#FFFFFF',
    borderRadius: '24px',
    width: '100%',
    maxWidth: '400px',
    padding: '24px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#0F172A',
    margin: 0,
  },
  closeBtn: {
    fontSize: '24px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: '#94A3B8',
  },
  modalSection: {
    marginBottom: '20px',
  },
  sectionTitle: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '700',
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '10px',
  },
  optionGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  option: {
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1px solid #E2E8F0',
    background: '#F8FAFC',
    textAlign: 'left',
    fontSize: '14px',
    color: '#475569',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  optionActive: {
    background: '#EFF6FF',
    borderColor: '#1D4ED8',
    color: '#1D4ED8',
  },
  applyBtn: {
    width: '100%',
    padding: '14px',
    borderRadius: '12px',
    border: 'none',
    background: '#1D4ED8',
    color: '#FFFFFF',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(29, 78, 216, 0.2)',
  }
};

export default PartiesHome;
